import { useSelector } from 'react-redux';
import { useTitle } from 'react-use';
import { useMemo } from 'react';
import type { RootState } from '../../store/rootReducer';
import Summary from './Summary';
import TopVideos from './TopVideos';
import TopNavPageTitle from '../../components/TopNavPageTitle';
import useNetworkFilter from '../../components/FilterBar/hooks/useNetworkFilter';
import type {
  CampaignFilterProps,
  CustomTagsFilterProps,
  NetworkFilterProps,
} from '../../components/FilterBar/Filters';
import { Page } from '../../utils/enums';
import { daysBetween } from './utils';
import useCustomTagsFilter from '../../components/FilterBar/hooks/useCustomTagsFilter';
import useCampaignFilter from '../../components/FilterBar/hooks/useCampaignFilter';
import useCampaignTypeFilter from '../../components/FilterBar/hooks/useCampaignTypeFilter';

const Dashboard: React.VFC = () => {
  const projectName = useSelector((state: RootState) => state.project.name);
  const startDate = useSelector((state: RootState) => state.filters.startDate);
  const endDate = useSelector((state: RootState) => state.filters.endDate);

  useTitle(`Overview - ${projectName}`);

  const {
    campaignFilterOptions,
    isLoading: isCampaignFilterLoading,
    hasData: hasCampaignFilterData,
    campaignFilterOnChange,
  } = useCampaignFilter({ page: Page.Dashboard });

  const campaignFilter = useMemo(
    () =>
      ({
        options: campaignFilterOptions,
        loading: isCampaignFilterLoading,
        hasData: hasCampaignFilterData,
        onChange: campaignFilterOnChange,
      } as CampaignFilterProps),
    [campaignFilterOptions, isCampaignFilterLoading, hasCampaignFilterData, campaignFilterOnChange]
  );

  const campaignTypeFilter = useCampaignTypeFilter();

  const {
    customTagsFilterOptions,
    customTagsFilterOnChange,
    isLoading: isCustomTagsFilterLoading,
  } = useCustomTagsFilter('default');
  const customTagsFilter: CustomTagsFilterProps = useMemo(
    () => ({
      options: customTagsFilterOptions,
      onChange: customTagsFilterOnChange,
      loading: isCustomTagsFilterLoading,
    }),
    [customTagsFilterOnChange, customTagsFilterOptions, isCustomTagsFilterLoading]
  );

  const {
    networkFilterOptions,
    networkFilterOnChange,
    isLoading: isNetworkFilterLoading,
  } = useNetworkFilter(Page.Dashboard);
  const networkFilter = useMemo(
    () =>
      ({
        options: networkFilterOptions,
        onChange: networkFilterOnChange,
        loading: isNetworkFilterLoading,
      } as NetworkFilterProps),
    [networkFilterOptions, networkFilterOnChange, isNetworkFilterLoading]
  );

  const daysOfDataToConsider = daysBetween(new Date(startDate), new Date(endDate));
  const isFiltersLoading =
    isNetworkFilterLoading || isCustomTagsFilterLoading || isCampaignFilterLoading || campaignTypeFilter.loading;

  return (
    <>
      <TopNavPageTitle
        title="Overview"
        subtitle={`Quickly get your most important concepts and performance results from the last ${daysOfDataToConsider} days of data.`}
        showFilterBar
        networkFilter={networkFilter}
        customTagsFilter={customTagsFilter}
        campaignFilter={campaignFilter}
        campaignTypeFilter={campaignTypeFilter}
        eventPrefix="dashboard"
        withDateFilter
      />
      <Summary filtersLoading={isFiltersLoading} />
      <TopVideos filtersLoading={isFiltersLoading} />
    </>
  );
};

export default Dashboard;
